import { useState } from 'react';
import {
  Check, X, Loader2, ChevronRight, AlertTriangle, FileText, Terminal,
  Search, Globe, FolderOpen, Wrench, Copy
} from 'lucide-react';
import type { ToolCall, ToolCallStatus } from '@/lib/agents/types';
import { getTool } from '@/lib/tools/ToolRegistry';
import { cn, formatTime } from '@/lib/utils';

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  read_file: FileText,
  write_file: FileText,
  run_command: Terminal,
  run_tests: Terminal,
  grep: Search,
  web_search: Globe,
  read_url: Globe,
  browser: Globe,
  open_folder: FolderOpen,
};

const STATUS_STYLE: Record<ToolCallStatus, string> = {
  pending: 'border-border text-fg-muted bg-surface',
  running: 'border-info/40 text-info bg-info/5',
  success: 'border-success/30 text-success bg-success/5',
  error: 'border-danger/40 text-danger bg-danger/5',
  requires_approval: 'border-warning/50 text-warning bg-warning/5',
};

const STATUS_LABEL: Record<ToolCallStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  success: 'Success',
  error: 'Error',
  requires_approval: 'Awaiting approval',
};

interface Props {
  toolCall: ToolCall;
}

export function ToolCallCard({ toolCall }: Props) {
  const def = getTool(toolCall.name);
  const Icon = ICONS[toolCall.name] ?? Wrench;
  const inputPreview = def?.inputPreview ? def.inputPreview(toolCall.input) : '';

  // Compact pending-approval rendering. The full review UX lives in ApprovalDialog —
  // this row is just a badge that tells the user one is waiting.
  if (toolCall.status === 'requires_approval') {
    return (
      <div className="my-2 flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/5 px-3 py-2">
        <Icon className="h-3.5 w-3.5 shrink-0 text-warning" />
        <span className="text-[13px] font-medium text-fg">
          {def?.label ?? toolCall.name}
        </span>
        {inputPreview && (
          <span className="truncate font-mono text-[12px] text-fg-muted">{inputPreview}</span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-warning/50 bg-warning/10 px-2 py-0.5 text-2xs font-medium text-warning">
          <AlertTriangle className="h-3 w-3" />
          Awaiting approval
        </span>
      </div>
    );
  }

  // Default expandable card for running/success/error/pending
  const initialOpen = toolCall.status === 'error';
  return <ExpandableToolCard toolCall={toolCall} initialOpen={initialOpen} Icon={Icon} inputPreview={inputPreview} />;
}

function ExpandableToolCard({
  toolCall, initialOpen, Icon, inputPreview,
}: {
  toolCall: ToolCall;
  initialOpen: boolean;
  Icon: React.ComponentType<{ className?: string }>;
  inputPreview: string;
}) {
  const def = getTool(toolCall.name);
  const [open, setOpen] = useState(initialOpen);

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-border bg-surface">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="focus-ring flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-surface-2"
        aria-expanded={open}
      >
        <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-fg-subtle transition-transform', open && 'rotate-90')} />
        <Icon className="h-3.5 w-3.5 shrink-0 text-fg-muted" />
        <span className="text-[13px] font-medium text-fg">{def?.label ?? toolCall.name}</span>
        {inputPreview && (
          <span className="truncate font-mono text-[12px] text-fg-muted">{inputPreview}</span>
        )}
        <span className={cn('pill ml-auto shrink-0', STATUS_STYLE[toolCall.status])}>
          {toolCall.status === 'running' && <Loader2 className="h-3 w-3 animate-spin" />}
          {toolCall.status === 'success' && <Check className="h-3 w-3" />}
          {toolCall.status === 'error' && <X className="h-3 w-3" />}
          {toolCall.status === 'pending' && <Loader2 className="h-3 w-3 animate-pulse-soft" />}
          {STATUS_LABEL[toolCall.status]}
        </span>
      </button>

      {open && (
        <div className="border-t border-border bg-surface/50 px-3 py-2.5">
          {toolCall.input != null && (
            <Section label="Input" copyable={prettyJson(toolCall.input)}>
              <CollapsibleText text={prettyJson(toolCall.input)} maxLines={10} />
            </Section>
          )}

          {toolCall.output != null && (
            <Section
              label="Output"
              copyable={
                typeof toolCall.output === 'string'
                  ? toolCall.output
                  : prettyJson(toolCall.output)
              }
            >
              <CollapsibleText
                text={
                  typeof toolCall.output === 'string'
                    ? toolCall.output
                    : prettyJson(toolCall.output)
                }
                maxLines={12}
              />
            </Section>
          )}

          {toolCall.error && (
            <div className="mt-1 rounded border border-danger/30 bg-danger/5 px-2.5 py-2 text-[12px] text-danger">
              {toolCall.error}
            </div>
          )}

          {toolCall.startedAt && (
            <div className="mt-2 flex items-center gap-2 text-2xs text-fg-subtle">
              <span>Started {formatTime(toolCall.startedAt)}</span>
              {toolCall.endedAt && (
                <>
                  <span>·</span>
                  <span>{((toolCall.endedAt - toolCall.startedAt) / 1000).toFixed(1)}s</span>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({
  label, copyable, children,
}: {
  label: string;
  copyable?: string;
  children: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    if (!copyable) return;
    try {
      await navigator.clipboard.writeText(copyable);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* noop */ }
  };
  return (
    <div className="mt-1 first:mt-0">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-2xs uppercase tracking-wide text-fg-subtle">{label}</span>
        {copyable && (
          <button
            type="button"
            onClick={onCopy}
            className="focus-ring rounded p-0.5 text-fg-subtle hover:text-fg"
            aria-label={`Copy ${label.toLowerCase()}`}
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function CollapsibleText({ text, maxLines }: { text: string; maxLines: number }) {
  const lineCount = text.split('\n').length;
  const overflows = lineCount > maxLines || text.length > 600;
  const [collapsed, setCollapsed] = useState(overflows);

  return (
    <div className="relative">
      <pre
        className="whitespace-pre-wrap break-words rounded bg-canvas px-2.5 py-2 font-mono text-[12px] text-fg"
        style={{ maxHeight: collapsed ? '160px' : 'none', overflow: 'hidden' }}
      >
        {text}
      </pre>
      {overflows && (
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className={cn(
            'focus-ring text-2xs font-medium text-fg-muted hover:text-fg',
            collapsed
              ? 'absolute inset-x-0 bottom-0 flex h-10 items-end justify-center bg-gradient-to-t from-canvas to-transparent pb-1'
              : 'mt-1 inline-flex'
          )}
        >
          {collapsed ? `Show ${lineCount} lines` : 'Collapse'}
        </button>
      )}
    </div>
  );
}

function prettyJson(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
